import React, { useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useFlags } from '@atlaskit/flag';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { token } from '@atlaskit/tokens';
import { getAtlassianStudioAutomationRuleEditUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { StudioAutomationCreateRuleExecutedData } from '@atlassian/conversation-assistant-message-actions-api/studio';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { AutomationCreateRuleCard } from './card/AutomationCreateRuleCard';
import messages from './messages';
import { RulePlanAnalytics } from './RulePlanAnalytics';

type AutomationCreateRuleActionOutput = Omit<
	StudioAutomationCreateRuleExecutedData,
	'ruleScope'
> & {
	ruleScope?: {
		resources?: string[];
	};
};

const STUDIO_SPACE_ARI_PREFIX = 'ari:cloud:studio::space/';

const parseStudioSpaceResource = (
	resource?: string,
): { workspaceId: string; spaceId: string } | undefined => {
	if (!resource?.startsWith(STUDIO_SPACE_ARI_PREFIX)) {
		return undefined;
	}

	const [workspaceId, spaceId, ...rest] = resource.slice(STUDIO_SPACE_ARI_PREFIX.length).split('/');
	if (!workspaceId || !spaceId || rest.length > 0) {
		return undefined;
	}

	return { workspaceId, spaceId };
};

export const StudioAutomationCreateRuleAction = (
	actionRenderer: ActionRendererFor<typeof ActionKeys.StudioAutomationCreateRule>,
): React.JSX.Element | null => {
	di(useAnalytics);

	const {
		actions,
		onCustomDataConfirm,
		getStatus,
		productKey,
		cloudId,
		ContentRenderer,
		conversationId,
		messageId,
	} = actionRenderer;
	const firstAction = actions[0];

	const { sendAnalyticsEvent } = useAnalytics();
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();

	const commonAttributes = useCallback(
		() => ({
			conversationId,
			invocationId: firstAction?.invocationId,
			messageId,
			actionKey: firstAction?.key,
		}),
		[conversationId, firstAction?.invocationId, firstAction?.key, messageId],
	);

	if (!firstAction) {
		return null;
	}

	const { overview = '', rule } = firstAction.data ?? {};

	const ruleName =
		(typeof rule?.name === 'string' ? rule.name : undefined) || formatMessage(messages.cardTitle);

	const status = getStatus(firstAction);
	const isDisabled =
		status !== 'idle' ||
		Boolean(
			firstAction.status && ['errored', 'cancelled', 'finished'].includes(firstAction.status),
		);
	const isSubmitting = status === 'started';

	const handleConfirm = async () => {
		// https://data-portal.internal.atlassian.com/analytics/registry/104742
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'createFlowButton',
			attributes: commonAttributes(),
		});

		try {
			const result = await onCustomDataConfirm<AutomationCreateRuleActionOutput>(
				firstAction,
				firstAction.data,
				(message) => {
					return (message.message_metadata as { actionOutput?: AutomationCreateRuleActionOutput })
						.actionOutput;
				},
			);
			const studioSpace = parseStudioSpaceResource(result?.ruleScope?.resources?.[0]);
			if (!result?.ruleUuid || !studioSpace) {
				throw new Error('Missing automation redirect data');
			}

			const editUrl = getAtlassianStudioAutomationRuleEditUrl(
				studioSpace.workspaceId,
				studioSpace.spaceId,
				result.ruleUuid,
			);

			// https://data-portal.internal.atlassian.com/analytics/registry/104745
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'createRule',
				actionSubject: 'rule',
				attributes: commonAttributes(),
			});
			window.location.assign(editUrl);
		} catch (error) {
			// https://data-portal.internal.atlassian.com/analytics/registry/104744
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'createFlowError',
				actionSubject: 'rule',
				attributes: commonAttributes(),
			});

			logException({
				exception: error instanceof Error ? error : new Error('Unknown error'),
				name: 'studio-automation-create-rule-action-button-error',
				tags: {
					component: 'StudioAutomationCreateRuleAction',
					actionKey: firstAction.key,
					invocationId: firstAction.invocationId,
				},
			});

			showFlag({
				title: formatMessage(messages.buildAutomationErrorTitle),
				icon: <CrossCircleIcon color={token('color.icon.danger')} label="" />,
				description: formatMessage(messages.buildAutomationErrorDescription),
				isAutoDismiss: true,
			});
		}
	};

	const handleError = (error: Error) => {
		logException({
			exception: error,
			name: 'studio-automation-create-rule-render-error',
			tags: {
				component: 'StudioAutomationCreateRuleAction',
				actionKey: firstAction.key,
				invocationId: firstAction.invocationId,
			},
		});
	};

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />} onError={handleError}>
			<RulePlanAnalytics actionRenderer={actionRenderer} />
			<AutomationCreateRuleCard
				ruleName={ruleName}
				overview={overview}
				ContentRenderer={ContentRenderer}
				onConfirm={handleConfirm}
				isSubmitting={isSubmitting}
				isDisabled={isDisabled}
				productKey={productKey}
				siteId={cloudId}
			/>
		</ErrorBoundary>
	);
};
